"use client";

import { usePathname } from "next/navigation";
import { useCallback, useState } from "react";

import { useFiltersStore } from "../stores/filters.store";
import { parseCatalogRoute } from "../types/catalogRoute.helper";
import type { CategoryChild, CategoryTree } from "../types/filters.type";

export interface UseCategoryFilterReturn {
  currentTree: CategoryTree;
  localSelection: string[];
  isAllSelected: boolean;
  canNavigateUp: boolean;
  navigateDown: (child: CategoryChild) => void;
  navigateUp: () => void;
  toggleItem: (id: string) => void;
  selectAll: () => void;
  selectRadio: (id: string) => void;
}

type TreeLike = {
  id: string | null;
  path?: string;
  level?: number;
  children?: { id: string; path: string }[];
};

function getSelectionFromPending(tree: TreeLike): {
  localSelection: string[];
  isAllSelected: boolean;
} {
  const { pendingFilters } = useFiltersStore.getState();
  const ids = pendingFilters?.categories_ids ?? [];
  const path = pendingFilters?.pending_category_path;

  if (tree.id && ids.includes(tree.id)) {
    return { localSelection: [], isAllSelected: true };
  }

  const fromIds = (tree.children ?? [])
    .filter((c) => ids.includes(c.id))
    .map((c) => c.id);
  if (fromIds.length > 0) {
    return { localSelection: fromIds, isAllSelected: false };
  }

  // pending_category_path aponta para o próprio tree (não um filho) → "Ver tudo"
  // Posicionado após o check de categories_ids para que seleção específica
  // (multi-item que escreve tree.path + ids simultaneamente) tenha prioridade.
  if (path && tree.path && path === tree.path && (tree.level ?? 0) >= 3) {
    return { localSelection: [], isAllSelected: true };
  }

  if (path) {
    const matchingChild = (tree.children ?? []).find(
      (c) => c.path === path || path.startsWith(c.path + "/"),
    );
    if (matchingChild) {
      return { localSelection: [matchingChild.id], isAllSelected: false };
    }
  }

  return { localSelection: [], isAllSelected: false };
}

export function useCategoryFilter(
  initialTree: CategoryTree,
): UseCategoryFilterReturn {
  const pathname = usePathname();

  const safeInitialTree: CategoryTree = {
    ...initialTree,
    children: initialTree.children ?? [],
  };

  const [currentTree, setCurrentTree] = useState<CategoryTree>(safeInitialTree);

  // Pilha de árvores ancestrais completas para permitir o drill up (Voltar)
  // nível a nível. O campo `parent` de CategoryTree é raso (sem children), por
  // isso guardamos a árvore inteira ao descer.
  const [history, setHistory] = useState<CategoryTree[]>([]);

  const [localSelection, setLocalSelection] = useState<string[]>(
    () => getSelectionFromPending(safeInitialTree).localSelection,
  );
  const [isAllSelected, setIsAllSelected] = useState<boolean>(
    () => getSelectionFromPending(safeInitialTree).isAllSelected,
  );

  const writeSelection = useCallback(
    (localSel: string[], allSel: boolean, tree: CategoryTree) => {
      const { updatePendingFilter, removePendingFilter } =
        useFiltersStore.getState();
      const isPathRoute = parseCatalogRoute(pathname).isCategoryDrill;

      if (tree.level >= 3) {
        if (allSel && tree.id) {
          if (!isPathRoute) {
            updatePendingFilter("categories_ids", [tree.id]);
          } else {
            updatePendingFilter("pending_category_path", tree.path);
            updatePendingFilter("categories_ids", []);
          }
          return;
        }

        const selectedChildren = (tree.children ?? []).filter((c) =>
          localSel.includes(c.id),
        );

        if (selectedChildren.length === 0) {
          removePendingFilter("pending_category_path", "");
          updatePendingFilter("categories_ids", []);
          return;
        }

        if (!isPathRoute) {
          updatePendingFilter(
            "categories_ids",
            selectedChildren.map((c) => c.id),
          );
          return;
        }

        if (selectedChildren.length === 1) {
          updatePendingFilter(
            "pending_category_path",
            selectedChildren[0].path,
          );
          updatePendingFilter("categories_ids", []);
        } else {
          updatePendingFilter("pending_category_path", tree.path);
          updatePendingFilter(
            "categories_ids",
            selectedChildren.map((c) => c.id),
          );
        }

        return;
      }

      if (localSel.length === 0) return;
      const selectedId = localSel[0];
      const selectedChild = (tree.children ?? []).find(
        (c) => c.id === selectedId,
      );
      if (!selectedChild) return;

      if (!isPathRoute) {
        updatePendingFilter("categories_ids", [selectedId]);
      } else {
        updatePendingFilter("pending_category_path", selectedChild.path);
      }
    },
    [pathname],
  );

  const navigateDown = useCallback(
    (child: CategoryChild) => {
      const newLevel = currentTree.level + 1;

      setHistory((prev) => [...prev, currentTree]);

      setCurrentTree((prev) => ({
        id: child.id,
        label: child.label,
        path: child.path,
        level: prev.level + 1,
        children: child.children ?? [],
        parent: { id: prev.id, label: prev.label, path: prev.path },
      }));

      const { localSelection: sel, isAllSelected: all } =
        getSelectionFromPending({
          id: child.id,
          path: child.path,
          level: newLevel,
          children: child.children ?? [],
        });
      setLocalSelection(sel);
      setIsAllSelected(all);
    },
    [currentTree],
  );

  const navigateUp = useCallback(() => {
    if (history.length === 0) return;

    const parentTree = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setCurrentTree(parentTree);

    // Subir é só navegação: a seleção continua no pending; recomputamos o
    // display a partir dele, sem reverter pendingFilters.
    const { localSelection: sel, isAllSelected: all } =
      getSelectionFromPending(parentTree);
    setLocalSelection(sel);
    setIsAllSelected(all);
  }, [history]);

  const toggleItem = useCallback(
    (id: string) => {
      const next = localSelection.includes(id)
        ? localSelection.filter((v) => v !== id)
        : [...localSelection, id];
      setIsAllSelected(false);
      setLocalSelection(next);
      writeSelection(next, false, currentTree);
    },
    [localSelection, currentTree, writeSelection],
  );

  const selectAll = useCallback(() => {
    setLocalSelection([]);
    setIsAllSelected(true);
    writeSelection([], true, currentTree);
  }, [currentTree, writeSelection]);

  const selectRadio = useCallback(
    (id: string) => {
      setLocalSelection([id]);
      writeSelection([id], false, currentTree);
    },
    [currentTree, writeSelection],
  );

  return {
    currentTree,
    localSelection,
    isAllSelected,
    canNavigateUp: history.length > 0,
    navigateDown,
    navigateUp,
    toggleItem,
    selectAll,
    selectRadio,
  };
}
